import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import params from '../config/Params';
import { getItemFromLocalStorage } from '../utils/storageUtils';
import { Helmet } from 'react-helmet';
import PendingDocumentsPopup from '../components/PendingDocumentsPopup';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

function Home() {

    const [tripCatgeory, setTripCategory] = useState([]);
    const [selectedCategoryData, setSelectedCategoryData] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [pendingListing, setPendingListing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [cancelCode, setCancelCode] = useState(null);
    const [isCancelPopupVisible, setIsCancelPopupVisible] = useState(false);
    const navigate = useNavigate();
    const logoImgRefs = useRef([]);
    const coverImgRefs = useRef([]);
    const loadedCount = useRef(0);

    const updateHeights = () => {
        logoImgRefs.current.forEach((logo, index) => {
            const cover = coverImgRefs.current[index];
            if (logo && cover) {
                const height = logo.offsetHeight;
                cover.style.height = `${height}px`;
            }
        });
    };

    useEffect(() => {
        const fetchTripCategory = async () => {
            const hostData = getItemFromLocalStorage('hostProfile');
            let formData = new FormData();
            formData.append('token', hostData.host_access_token);

            try {
                const response = await axios({
                    method: 'post',
                    headers: {
                      'Content-Type': 'multipart/form-data'
                    },
                    url: `${params.baseURL}${params.endpoints.getTripCategory}`,
                    data: formData
                });
            
                if (response.data.status === 'category') {
                    setTripCategory(response.data.trip_cat_list);
                    setSelectedCategory(response.data.trip_cat_list[0]?.category_id);
                    setLoading(false);
                } else {
                    setError('An error occurred while fetching data.');
                }
            } catch (err) {
                setError(err);
                setLoading(false);
            }
        };

        const fetchPendingListing = async () => {
            const hostData = getItemFromLocalStorage('hostProfile');
            let allItems = [];
            let currentPage = 1;
            let totalPages = 1;
        
            try {
                while (currentPage <= totalPages) {
                    let formData = new FormData();
                    formData.append('token', hostData.host_access_token);
                    formData.append('currentPage', currentPage);
        
                    const response = await axios({
                        method: 'post',
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        url: `${params.baseURL}${params.endpoints.getPendingListings}`,
                        data: formData,
                    });
        
                    if (response.data.items && Array.isArray(response.data.items)) {
                        allItems = [...allItems, ...response.data.items];
                        totalPages = response.data._meta.pageCount;
                        currentPage++;
                    } else {
                        throw new Error('Invalid response format');
                    }
                }
        
                const uniqueItems = Array.from(new Set(allItems.map(JSON.stringify))).map(JSON.parse);
        
                setPendingListing(uniqueItems);
                setLoading(false);
            } catch (err) {
                setError(err.message || 'An error occurred while fetching data.');
                setLoading(false);
            }
        };        

        fetchTripCategory();
        fetchPendingListing();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            handleCategoryChange(selectedCategory);
        }
    }, [selectedCategory]);

    const handlePendingListClick = (data) => {
        setSelectedDocuments(data);
        navigate('/listing-document-verification', { state: { data } });
    };

    const handleCategoryChange = async (categoryId) => {
        setSelectedCategory(categoryId);
        setCategoryLoading(true);
        const hostData = getItemFromLocalStorage('hostProfile');
        let formData = new FormData();
        formData.append('token', hostData.host_access_token);
        formData.append('category', categoryId);

        try {
            const response = await axios({
                method: 'post',
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                url: `${params.baseURL}${params.endpoints.getMyBooking}`,
                data: formData
            });

            if (response.data && response.data.items.length > 0) {
                setSelectedCategoryData(response.data.items);
            } else {
                setSelectedCategoryData([]);
            }
        } catch (err) {
            setError('An error occurred while fetching category data.');
            setSelectedCategoryData([]);
        } finally {
            setCategoryLoading(false);
        }
    };

    const openCancelPopup = (cnf_code) => {
        setCancelCode(cnf_code);
        setIsCancelPopupVisible(true);
    };

    const closeCancelPopup = () => {
        setIsCancelPopupVisible(false);
        setCancelCode(null);
    };

    const handleCancelBooking = async (code) => {
        const hostData = getItemFromLocalStorage('hostProfile');
        let formData = new FormData();
        formData.append('token', hostData.host_access_token);
        formData.append('code', code);

        try {
            const response = await axios({
                method: 'post',
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                url: `${params.baseURL}${params.endpoints.cancelBooking}`,
                data: formData
            });

            if (response.data.status === 'ok') {
                toast.success('Booking Cancelled Sucessfully!');
                window.location.reload();
            } else {
                toast.error('Unable to cancel booking. Try again')
            }
        } catch (err) {
            console.log(err)
        } finally {
            setIsCancelPopupVisible(false);
        }
    };

    const handleImageLoad = () => {
        loadedCount.current += 1;
        const totalImages = selectedCategoryData.length * 2;
        if (loadedCount.current >= totalImages) {
            updateHeights();
        }
    };

    useEffect(() => {
        loadedCount.current = 0;
        logoImgRefs.current = [];
        coverImgRefs.current = [];
    
        if (Array.isArray(selectedCategoryData) && selectedCategoryData.length > 0) {
            window.addEventListener('resize', updateHeights);
        }
    
        return () => {
            window.removeEventListener('resize', updateHeights);
        };
    }, [selectedCategoryData]);  

    if (loading) {
        return <p>Loading...</p>;
    }

    if (error) {
        return <p>Error loading category: {error.message}</p>;
    }

    return (
        <div>
          <Helmet>
            <title>Dashboard</title>
            <meta name="description" content="" />
          </Helmet>

          <div className='pending-listing-box'>
            {pendingListing.length > 0 ? (
                <>
                <div className='plbald'>
                    {pendingListing.map((data) => (
                        <div className='pldata-box' key={data.stay_listing_id} onClick={() => handlePendingListClick(data)}>
                            <div className='pldata-info-box'>
                                <div className='category-img-section'>
                                    <img className='img-fluid ciso'  
                                        src={data.cover_photo ? data.cover_photo : '/assets/img/default_listing.jpg'}
                                        alt='' 
                                    />
                                    <img src={data.category_info.category_image_url} alt='' className='cist' />
                                </div>
                                <div className='pltxt-box'>
                                    <div className='cat-pointer'>
                                        <img src={data.property_title_icon_image} alt="Profile" className="pointer-icon" />
                                        <div className='wspl-ta-section' style={{width:'300px'}}>
                                            <span className='wspl-title-box'>{data.listing_title || 'Unknown Title'}</span>
                                            <span className='wspl-add-box'>{data.property_address}</span>
                                        </div>
                                    </div>
                                    {/*<div className='cat-pointer'>
                                        <img src={'/assets/img/icons/pointer_icon.svg'} alt="Profile" className="pointer-icon" />
                                        <span><strong>Verify Owner's identity</strong></span>
                                    </div>*/}
                                    <div className='catp-box'>
                                        <div className='cat-pointer mb-0'>
                                            <img src={'/assets/img/icons/pending_verification_icon.svg'} alt="Profile" className="pointer-icon" />
                                            <span className='plpvrtb'>Pending: Verify Owner's identity</span>
                                        </div>
                                        <div>
                                            <i class="fas fa-chevron-right"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/*<div className='pldata-btn-block'>
                                <button className='btn btn-black'>Start Verification</button>
                            </div>*/}
                        </div>
                    ))}
                </div>

                <div className='vapldps'>
                    <a href='/pending-document-list/' className='vapldps-btn' style={{alignContent:'center'}}>Pending All Verification</a>
                </div>
                </>
            ) : (
                <></>
            )}
          </div>

          <div className='filter-box'>
            {tripCatgeory.length > 0 ? (
            <div className='filter-booking-box'>
                {tripCatgeory.map((category) => (
                    <label class="filter-booking" key={category.category_id}>
                        <input type="radio" name="bookingData" id="booking_data" 
                            class="filter__radio input" 
                            value={category.category_id} 
                            checked={selectedCategory === category.category_id}
                            onChange={() => handleCategoryChange(category.category_id)}
                        />
                        <span>{category.category_name}</span>
                    </label>
                ))}
            </div>
            ) : (
                <p>No catgeory found</p>
            )}
          </div>

          <div className="category-data-section">
            {categoryLoading ? (
                <p></p>
            ) : selectedCategoryData && selectedCategoryData.length > 0 ? (
                <div className='category-section' style={{gap:'50px'}}>
                    {selectedCategoryData.map((item, index) => (
                        <a href={`/booking-details/${item.stay_booking_id}/${selectedCategory}`} className='category-data' style={{padding: '0', textTransform:'none', boxShadow:'none'}} key={index}>
                            <div className='category-img-sections'>
                                <div className="apimgcibox"  style={{boxShadow:'none', padding:'0'}}>
                                    <div className='image-wrapper lcibox' style={{width:'100%'}}>
                                        <div className='ac-cover-image'>
                                            <img
                                                ref={el => coverImgRefs.current[index] = el}
                                                src={item.cover_photo || '/assets/img/default_listing.jpg'}
                                                alt={item.listing_title || 'Untitled Listing'}
                                                className="ciso ap-ciso"
                                                style={{height:'180px', borderRadius:'5px'}}
                                                onLoad={handleImageLoad}
                                            />
                                            {/*<div className='aci-overlay'></div>*/}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/*<Card style={{marginBottom: '1rem'}}>
                                <Box sx={{ display: 'flex' }} style={{gap: '10px'}}>
                                    <img src={item.cover_photo || '/assets/img/default_listing.jpg'} className="img-fluid card-pro-img" />
                                    <img src={'/assets/img/layout.png'} className='img-fluid card-cat-img' />
                                </Box>
                            </Card>*/}
                            <div>
                                <p>Booking Details</p>
                                <div className='hpbdpd'>
                                    <div className='category-data-header cat-pointer' style={{padding:'0 12px'}}>
                                        <img src={item.property_title_icon_image} alt="Profile" className="pointer-icon" />
                                        <div className='hbdhddiv'>
                                            <span>{item.listing_title}</span>
                                        </div>
                                    </div>
                                    <div className='category-data-header cat-pointer' style={{padding:'0 12px'}}>
                                        <img src={'/assets/img/icons/common_point_icon.png'} alt="Profile" className="pointer-icon" />
                                        <div className='hbdhddiv'>
                                            <span>Booking ID </span><span>{item.confirmation_code}</span>
                                        </div>
                                    </div>
                                    {/*<div className='cat-pointer'>
                                        <img src={'/assets/img/icons/common_point_icon.png'} alt="Profile" className="pointer-icon" />
                                        <div className='hbdhddiv'>
                                            <span>Dates</span>
                                            <div><span>{item.checkin_date_short}</span> - <span>{item.checkout_date_short}</span></div>
                                        </div>
                                    </div>*/}
                                    <div class="check-container" style={{marginBottom:'10px'}}>
                                        <div class="hbd-check-card hbcc-checkIn" style={{padding:'10px'}}>
                                            <p><img src={'/assets/img/icons/ws_checkin_checkout_icon_3.svg'} alt='' className='img-fluid cc-img' /> <strong>CHECK-IN</strong></p>
                                            <p className='ccdbox'><img src={'/assets/img/icons/ws_date_time_icon.svg'} alt='' className='img-fluid cc-img' /> {item.formatted_checkin_date}</p>
                                            <p className='ccdbox'><img src={'/assets/img/icons/ws_date_time_icon.svg'} alt='' className='img-fluid cc-img' /> {item.formatted_checkin_time}</p>
                                        </div>

                                        <div class="divider"></div>

                                        <div class="hbd-check-card hbcc-checkOut" style={{padding:'10px'}}>
                                            <p><strong>CHECK-OUT</strong> <img src={'/assets/img/icons/ws_checkin_checkout_icon_3.svg'} alt='' className='img-fluid cc-img' /></p>
                                            <p className='ccdbox'>{item.formatted_checkout_date} <img src={'/assets/img/icons/ws_date_time_icon.svg'} alt='' className='img-fluid cc-img' /></p>
                                            <p className='ccdbox'>{item.formatted_checkout_time} <img src={'/assets/img/icons/ws_date_time_icon.svg'} alt='' className='img-fluid cc-img' /></p>
                                        </div>
                                    </div>
                                    <div className='cat-pointer' style={{padding:'0 12px'}}>
                                        <img src={'/assets/img/icons/common_point_icon.png'} alt="Profile" className="pointer-icon" />
                                        <div className='hbdhddiv'>
                                            <span>Smart Check-in </span><span>{item.actual_web_checkin_datetime_text || 'Not completed'}</span>
                                        </div>
                                    </div>
                                    {/*<div className='cat-pointer'>
                                        <img src={'/assets/img/icons/common_point_icon.png'} alt="Profile" className="pointer-icon" />
                                        <div className='hbdhddiv'>
                                            <span>Checkin time </span><span>{item.actual_checkin_datetime || 'Not completed'}</span>
                                        </div>
                                    </div>
                                    <div className='cat-pointer'>
                                        <img src={'/assets/img/icons/common_point_icon.png'} alt="Profile" className="pointer-icon" />
                                        <div className='hbdhddiv'>
                                            <span>Checkout time </span><span>{item.actual_checkout_datetime || 'Not completed'}</span>
                                        </div>
                                    </div>*/}
                                    <div className='cat-pointer' style={{padding:'0 12px'}}>
                                        <img src={'/assets/img/icons/common_point_icon.png'} alt="Profile" className="pointer-icon" />
                                        <div className='hbdhddiv'>
                                            <span style={{ color: 'red' }}>Payment Details</span>
                                            <span style={{ color: 'red' }}>{item.payment_via ? item.payment_via : 'Not available'}</span>
                                        </div>
                                    </div>
                                </div>

                                <hr style={{marginBottom:'5px'}}></hr>
                                <div className='category-user-box'>
                                    <div className='category-utxt-box'>
                                        <div>
                                            <img src={item.guest_profile_image} alt="Profile" className="user-circle" style={{borderRadius:'50%'}} />
                                        </div>
                                        <div>
                                            <p>{item.customer_name}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <i class="fas fa-chevron-right"></i>
                                    </div>
                                </div>
                                {selectedCategory === 1 && (
                                    <>
                                    <hr style={{marginTop:'5px'}}></hr>
                                    {/*<div className='chinchous mb-2'>
                                        {!item.actual_checkin_datetime && (
                                            <Link className="btn" to={`/checkin/${item.confirmation_code}`}>
                                                Check-in
                                            </Link>
                                        )}
                                        {item.actual_checkin_datetime && !item.actual_checkout_datetime && (
                                            <Link className="btn" to={`/checkout/${item.confirmation_code}`}>
                                                Check-out
                                            </Link>
                                        )}
                                        <button 
                                            className="btn" 
                                            id={item.confirmation_code}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openCancelPopup(item.confirmation_code);
                                            }}
                                        >
                                            Cancel Booking
                                        </button>
                                    </div>*/}

                                    <div class="dual-button">
                                        {!item.actual_checkin_datetime && (
                                            <Link className="btn check-in active" to={`/checkin/${item.confirmation_code}`}>
                                                <i class="fa fa-check-circle"></i> Check-In
                                            </Link>
                                        )}
                                        {item.actual_checkin_datetime && !item.actual_checkout_datetime && (
                                            <Link className="btn check-in active" to={`/checkout/${item.confirmation_code}`}>
                                                <i class="fa fa-check-circle"></i> Check-out
                                            </Link>
                                        )}
                                        <button 
                                            class="btn cancel-booking"
                                            id={item.confirmation_code}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openCancelPopup(item.confirmation_code);
                                            }}
                                        >
                                            Cancel Booking
                                        </button>
                                    </div>
                                    </>
                                )}
                                {(selectedCategory === 2 || selectedCategory === 3) && (
                                    <>
                                    <hr style={{marginTop:'5px'}}></hr>
                                    <div className='chinchous mb-2'>
                                        <Link className="btn" to={`/booking-details/${item.stay_booking_id}/${selectedCategory}`}>
                                            View Details
                                        </Link>
                                    </div>
                                    </>
                                )}
                            </div>
                        </a>
                    ))}
                </div>
            ) : (
                <div className='nbfcb'>
                    {selectedCategory === 4 && (
                        <img src={'/assets/img/placeholder/no_pending_request_1.svg'} className='empty-image img-fluid' alt='' />
                    )}
                    {selectedCategory === 1 && (
                        <img src={'/assets/img/placeholder/no_booking_yet.svg'} className='empty-image img-fluid' alt='' />
                    )}
                    {selectedCategory === 2 && (
                        <img src={'/assets/img/placeholder/no_completed_booking.svg'} className='empty-image img-fluid' alt='' />
                    )}
                    {selectedCategory === 3 && (
                        <img src={'/assets/img/placeholder/no_cancelled_booking.svg'} className='empty-image img-fluid' alt='' />
                    )}
                </div>
            )}
          </div>

          {/* Confirmation Popup */}
          {isCancelPopupVisible && (
            <div className="confirmation-popup-overlay">
                <div className="confirmation-popup">
                <h4>Confirm Cancel</h4>
                <p>Are you sure to cancel this booking with Booking ID - {cancelCode}?</p>
                <div className="popup-actions">
                    <button
                    className="btn btn-danger"
                    onClick={() => handleCancelBooking(cancelCode)}
                    >
                        Go Ahead
                    </button>
                    <button className="btn btn-secondary" onClick={closeCancelPopup}>
                        Close
                    </button>
                </div>
                </div>
            </div>
          )}

          <PendingDocumentsPopup 
              show={showPopup} 
              onClose={() => setShowPopup(false)} 
              data={selectedDocuments} 
          />

        </div>
    );
}

export default Home;